import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { ERROR_MESSAGES } from '../date-time-helpers.constants';
import type { DateInput, DateValidation } from '../date-time-helpers.type';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

/**
 * Safely parses various date input types and returns a Dayjs object
 * This is the core parsing function used throughout the helpers
 *
 * @param {DateInput} input - Date input to parse
 * @returns {Dayjs | null} Dayjs object or null if parsing fails
 */
const safeParseDateInput = (input: DateInput): Dayjs | null => {
  if (!input) {
    return null;
  }

  // If it's already a Dayjs object, return it
  if (dayjs.isDayjs(input)) {
    return input;
  }

  // If it's a Date object, convert it
  if (input instanceof Date) {
    const result = dayjs(input);
    return result.isValid() ? result : null;
  }

  // If it's a string, try to parse it
  if (typeof input === 'string') {
    const result = dayjs(input);
    if (result.isValid()) {
      return result;
    }
  }

  return null;
};

/**
 * Parses a date string and validates it
 *
 * @param {DateInput} input - Date input to validate
 * @param {Dayjs | null} fallback - Optional fallback value if parsing fails
 * @returns {DateValidation} Validation result with parsed date
 */
const validateAndParseDate = (input: DateInput, fallback: Dayjs | null = null): DateValidation => {
  if (!input) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.INVALID_DATE,
      parsedDate: fallback,
    };
  }

  const parsedDate = safeParseDateInput(input);

  if (!parsedDate) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.PARSE_ERROR,
      parsedDate: fallback,
    };
  }

  return {
    isValid: true,
    parsedDate,
  };
};

/**
 * Checks if a date input is valid
 *
 * @param {DateInput} input - Date input to validate
 * @returns {boolean} Whether the date is valid
 */
const isValidDate = (input: DateInput): boolean => {
  const validation = validateAndParseDate(input);

  return validation.isValid;
};

/**
 * Parses a date string and converts it to UTC
 *
 * @param {DateInput} input - Date input to parse
 * @returns {Dayjs | null} UTC Dayjs object or null
 */
const parseToUTC = (input: DateInput): Dayjs | null => {
  const parsed = safeParseDateInput(input);

  return parsed ? parsed.utc() : null;
};

/**
 * Parses a date string and converts it to local timezone
 *
 * @param {DateInput} input - Date input to parse
 * @returns {Dayjs | null} Local Dayjs object or null
 */
const parseToLocal = (input: DateInput): Dayjs | null => {
  const parsed = safeParseDateInput(input);

  return parsed ? parsed.local() : null;
};

export { isValidDate, parseToLocal, parseToUTC, safeParseDateInput, validateAndParseDate };
